/**
 * EE AI 模組前端服務（呼叫 /api/ee/ai/*）
 * Phase 5：Provider 管理、Completion 測試、使用記錄
 */
import axios from '../api/axios';

const BASE = '/api/ee/ai';

// ============================================================
// Types — Provider
// ============================================================

export interface AiProvider {
  id: number;
  provider_code: string;
  provider_name: string;
  provider_type: 'ollama' | 'openai' | 'anthropic';
  base_url: string;
  default_model: string;
  request_timeout_sec: number;
  is_active: boolean;
  is_default: boolean;
  has_api_key: boolean;
  last_test_at: string | null;
  last_test_status: 'success' | 'failed' | null;
  last_test_message: string | null;
  // Phase 7 多租戶
  share_all_tenants: boolean;       // 平台級共享（mana 才能 toggle）
  is_owner: boolean;                 // 該 user 的租戶是否為 provider 擁有者
  is_active_for_tenant: boolean;     // 該 user 的租戶在自家視角是否啟用
  via_share_all: boolean;            // 透過 share_all_tenants 看到（非顯式 grant）
  // 4-pre-c · key 來源 + BYO 分潤
  key_source: 'platform' | 'tenant_byo';
  provided_by_org_id: number | null;
  share_allowed: boolean;
  edit_by: number;
  created_at: string;
  updated_at: string;
}

export interface ProviderOrgLink {
  organization_id: number;
  is_owner: boolean;
  is_active_for_tenant: boolean;
  granted_at: string;
  granted_by: number | null;
}

export interface AiProviderCreate {
  provider_code: string;
  provider_name: string;
  provider_type: 'ollama' | 'openai' | 'anthropic';
  base_url: string;
  api_key?: string;
  default_model: string;
  request_timeout_sec: number;
  is_active: boolean;
  is_default: boolean;
  // 4-pre-c
  key_source: 'platform' | 'tenant_byo';
  provided_by_org_id?: number | null;
  share_allowed: boolean;
}

export interface AiProviderUpdate {
  provider_name?: string;
  provider_type?: string;
  base_url?: string;
  api_key?: string;
  default_model?: string;
  request_timeout_sec?: number;
  is_active?: boolean;
  is_default?: boolean;
  key_source?: 'platform' | 'tenant_byo';
  provided_by_org_id?: number | null;
  share_allowed?: boolean;
}

export interface AiProviderTestResponse {
  success: boolean;
  message: string;
  models: string[];
}

// ============================================================
// Types — Completion
// ============================================================

export interface AiCompleteRequest {
  prompt: string;
  system_prompt?: string;
  model?: string;
  provider_id?: number;
  max_tokens?: number;
  temperature?: number;
}

export interface AiCompleteResponse {
  content: string;
  model_name: string;
  provider_code: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  duration_ms: number;
}

// ============================================================
// Types — Usage
// ============================================================

// Phase 8 蒸餾欄位也加進這個 type（list endpoint 也會回）
export interface AiUsageLog {
  feature_code?: string | null;
  organization_id?: number | null;
  id: number;
  provider_id: number | null;
  provider_code: string;
  model_name: string;
  caller_type: string;
  caller_ref: string | null;
  user_id: number | null;
  prompt_preview: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  duration_ms: number | null;
  status: 'success' | 'failed';
  error_message: string | null;
  created_at: string;
}

export interface AiUsageStats {
  total_calls: number;
  success_calls: number;
  failed_calls: number;
  success_rate: number;
  total_tokens: number;
  avg_duration_ms: number | null;
  by_model: { model_name: string; calls: number; tokens: number }[];
  by_caller_type: { caller_type: string; calls: number }[];
}

export interface PaginatedUsageLogs {
  items: AiUsageLog[];
  total: number;
  page: number;
  page_size: number;
}

// ============================================================
// Provider API
// ============================================================

export const listAiProviders = async (): Promise<AiProvider[]> => {
  const res = await axios.get(`${BASE}/providers/`);
  return res.data;
};

// post-a · AI Office 助手 Layer 3 UI gate probe（訂閱 approved + provider active）
export const checkOfficeChatAvailable = async (): Promise<{ available: boolean; reason?: string }> =>
  (await axios.get(`${BASE}/office-chat/available`)).data;

export const createAiProvider = async (data: AiProviderCreate): Promise<AiProvider> => {
  const res = await axios.post(`${BASE}/providers/`, data);
  return res.data;
};

export const updateAiProvider = async (id: number, data: AiProviderUpdate): Promise<AiProvider> => {
  const res = await axios.put(`${BASE}/providers/${id}`, data);
  return res.data;
};

export const deleteAiProvider = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/providers/${id}`);
};

export const testAiProvider = async (id: number): Promise<AiProviderTestResponse> => {
  const res = await axios.post(`${BASE}/providers/${id}/test`);
  return res.data;
};

export const listAiProviderModels = async (id: number): Promise<string[]> => {
  const res = await axios.get(`${BASE}/providers/${id}/models`);
  return res.data.models;
};

export const setDefaultAiProvider = async (id: number): Promise<AiProvider> => {
  const res = await axios.post(`${BASE}/providers/${id}/set_default`);
  return res.data;
};

// ============================================================
// Phase 7：多租戶 — 共享 / 啟停 / 全平台共享
// ============================================================

export const setProviderTenantActive = async (
  id: number, isActiveForTenant: boolean,
): Promise<AiProvider> => {
  const res = await axios.put(`${BASE}/providers/${id}/tenant-active`,
                              null,
                              { params: { is_active_for_tenant: isActiveForTenant } });
  return res.data;
};

export const setProviderShareAllTenants = async (
  id: number, enabled: boolean,
): Promise<AiProvider> => {
  const res = await axios.put(`${BASE}/providers/${id}/share-all-tenants`,
                              null,
                              { params: { enabled } });
  return res.data;
};

export const listProviderOrgs = async (id: number): Promise<ProviderOrgLink[]> => {
  const res = await axios.get(`${BASE}/providers/${id}/orgs`);
  return res.data;
};

export const shareProviderToOrg = async (
  providerId: number, orgId: number,
): Promise<{ ok: boolean; provider_id: number; organization_id: number }> => {
  const res = await axios.post(`${BASE}/providers/${providerId}/share/${orgId}`);
  return res.data;
};

export const revokeProviderShare = async (
  providerId: number, orgId: number,
): Promise<void> => {
  await axios.delete(`${BASE}/providers/${providerId}/share/${orgId}`);
};

// ============================================================
// Phase 8 蒸餾基建（admin only）
// ============================================================

export type QualityLabel = 'good' | 'bad' | 'edited';

export interface UsageLogFull {
  id: number;
  provider_code: string;
  model_name: string;
  feature_code: string | null;
  organization_id: number | null;
  user_id: number | null;
  prompt_full: string | null;
  output_full: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  duration_ms: number | null;
  status: string;
  error_message: string | null;
  created_at: string;
  sample_id: number | null;
  quality_label: QualityLabel | null;
  corrected_output: string | null;
  notes: string | null;
}

export interface TrainingSample {
  id: number;
  usage_log_id: number;
  organization_id: number;
  feature_code: string | null;
  quality_label: QualityLabel;
  corrected_output: string | null;
  notes: string | null;
  reviewer_id: number;
  reviewed_at: string;
  updated_at: string;
}

export interface PaginatedTrainingSamples {
  total: number;
  page: number;
  page_size: number;
  items: TrainingSample[];
}

export const getUsageLogFull = async (id: number): Promise<UsageLogFull> => {
  const res = await axios.get(`${BASE}/training/usage/${id}/full`);
  return res.data;
};

export const listTrainingSamples = async (params?: {
  quality_label?: QualityLabel;
  feature_code?: string;
  page?: number;
  page_size?: number;
}): Promise<PaginatedTrainingSamples> => {
  const res = await axios.get(`${BASE}/training/samples`, { params });
  return res.data;
};

export const reviewTrainingSample = async (data: {
  usage_log_id: number;
  quality_label: QualityLabel;
  corrected_output?: string | null;
  notes?: string | null;
}): Promise<{ id: number; usage_log_id: number; quality_label: QualityLabel; reviewed_at: string }> => {
  const res = await axios.post(`${BASE}/training/samples`, data);
  return res.data;
};

export const deleteTrainingSample = async (sampleId: number): Promise<void> => {
  await axios.delete(`${BASE}/training/samples/${sampleId}`);
};

export const exportTrainingJsonlUrl = (
  label: 'good' | 'edited' | 'all' = 'good',
  feature_code?: string,
): string => {
  const params = new URLSearchParams();
  params.set('label', label);
  if (feature_code) params.set('feature_code', feature_code);
  const baseURL = (axios.defaults.baseURL || '').replace(/\/$/, '');
  return `${baseURL}${BASE}/training/samples/export?${params.toString()}`;
};

// ============================================================
// Completion API（管理員測試）
// ============================================================

export const aiComplete = async (data: AiCompleteRequest): Promise<AiCompleteResponse> => {
  const res = await axios.post(`${BASE}/complete/`, data);
  return res.data;
};

// ============================================================
// Usage API
// ============================================================

export const listAiUsageLogs = async (params?: {
  provider_id?: number;
  caller_type?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}): Promise<PaginatedUsageLogs> => {
  const res = await axios.get(`${BASE}/usage/`, { params });
  return res.data;
};

export const getAiUsageStats = async (days = 30): Promise<AiUsageStats> => {
  const res = await axios.get(`${BASE}/usage/stats`, { params: { days } });
  return res.data;
};
